/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

// what it do:
//
// * detects changes to linked inputs and evaluates the local input value
//   * This may result in clearing the value if it is no longer valid
// * updates options list / filtering
//
class DistilledInputHandler extends PictSectionInputExtension
{
	/**
	 * @param {import('fable')} pFable - The Fable instance.
	 * @param {Record<string, any>} [pOptions] - The options for the provider.
	 * @param {string} [pServiceHash] - The service hash for the provider.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
	}
	/**
	 * Called when the data change function is called
	 *
	 * Called when an input has this Provider hash in its 'Providers' array.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The new value of the input object
	 * @param {string} pHTMLSelector - The HTML selector for the input object
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (!this._suppressDistillingEmit)
		{
			this._emitDistillingDataChangeEvent(pInput.PictForm.DistillingKey, pValue || '');
		}
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Called when an input has this Provider hash in its 'Providers' array.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The new value of the input object
	 * @param {string} pHTMLSelector - The HTML selector for the input object
	 * @param {number} pRowIndex - The row index of the tabular data
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 */
	onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		//TODO: any special handling for tabular / rows? ex. distilling that is row-local?
		if (!this._suppressDistillingEmit)
		{
			this._emitDistillingDataChangeEvent(pInput.PictForm.DistillingKey, pValue || '');
		}
		return super.onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * NOTE: You can pass in a date value with the :date flag in the DistillingDisplay field to format date fields.
	 * TODO: add tabular version...
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		const tmpResult = super.onEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
		if (typeof pEvent !== 'string')
		{
			return tmpResult;
		}
		const tmpEventParts = pEvent.split(':');
		const tmpEventHash = tmpEventParts[0];
		if (tmpEventHash !== 'GetPickList')
		{
			return tmpResult;
		}
		const tmpListHash = tmpEventParts[1];
		if (tmpListHash !== pInput.PictForm.SelectOptionsPickList)
		{
			return tmpResult;
		}
		const tmpListDataAddress = tmpEventParts[2];
		const tmpEventOptions = JSON.parse(tmpEventParts.slice(3).join(':'));
		this.pict.log.info(`CustomSelectInputProvider received event: ${pEvent} for list ${tmpListHash} with options:`, { tmpEventHash, tmpListHash, tmpListDataAddress, tmpEventOptions });
		const tmpListData = this.pict.manifest.getValueByHash(this.pict, tmpListDataAddress);
		if (!Array.isArray(tmpListData))
		{
			this.pict.log.error(`CustomSelectInputProvider expected array data at address ${tmpListDataAddress} but found:`, { tmpListData });
			return tmpResult;
		}
		if (tmpListData.length > 0)
		{
			// this happens when multiple inputs share the same picklist - don't want to double-populate
			return tmpResult;
		}
		/** @type {import('@paviasystems/grafee').Grafee} */
		const tmpGraph = this.pict.providers.DistillingBundleManager.getLatestGraph(pInput.PictForm.DistillingBundleHash);
		let tmpAvailableValues;
		if (tmpEventOptions.extractRootKey)
		{
			tmpAvailableValues = Array.isArray(tmpEventOptions.extractRootKey) ? tmpEventOptions.extractRootKey : [ tmpEventOptions.extractRootKey ];
		}
		else
		{
			const tmpSearchSelections = this._getSelectedDistillingValues(pView, pInput.PictForm.DistillingBundleHash, pInput.PictForm.DistillingKey);
			if (pInput.PictForm?.IgnoreKeys)
			{
				for (let k of pInput.PictForm.IgnoreKeys)
				{
					if (tmpSearchSelections[k])
					{
						delete tmpSearchSelections[k];
					}
				}
			}
			tmpAvailableValues = tmpGraph.searchValues(pInput.PictForm.DistillingKey, tmpEventOptions, tmpSearchSelections);
		}
		const tmpDisplayFields = (pInput.PictForm.DistillingDisplay || '').split(',').filter((v) => v !== '');
		const tmpDisplayFieldsWithDateFlags = [];
		// for each tmpDisplayFields, check for a `:date` flag at the end of the key and remove it/store it for formatting
		if (tmpDisplayFields.length > 0)
		{
			for (let i = 0; i < tmpDisplayFields.length; i++)
			{
				const tmpFieldParts = tmpDisplayFields[i].split(':');
				if (tmpFieldParts.length > 1 && tmpFieldParts[tmpFieldParts.length - 1] === 'date')
				{
					// store as date field
					tmpDisplayFieldsWithDateFlags.push({ field: tmpFieldParts.slice(0, -1).join(':'), isDate: true });
					// update original array to remove :date flag
					tmpDisplayFields[i] = tmpFieldParts.slice(0, -1).join(':');
				}
				else
				{
					tmpDisplayFieldsWithDateFlags.push({ field: tmpFieldParts.join(':'), isDate: false });
				}
			}
		}
		const tmpMappedValues = tmpGraph.extractJoinedValues(pInput.PictForm.DistillingKey, tmpAvailableValues, tmpDisplayFields);
		let tmpArray = [];
		const usedKeys = {};
		for (const tmpMappedValue of tmpMappedValues)
		{
			const displayParts = [];
			for (const tmpDisplayField of tmpDisplayFieldsWithDateFlags)
			{
				let fieldValue = tmpMappedValue[tmpDisplayField.field];
				// for date fields, format as YYYY-MM-DD
				if (tmpDisplayField.isDate && fieldValue)
				{
					const tmpDate = new Date(fieldValue);
					if (!isNaN(tmpDate.getTime()))
					{
						fieldValue = this.pict.Dates.dayJS(tmpDate).format('YYYY-MM-DD');
					}
				}
				if (fieldValue)
				{
					displayParts.push(fieldValue);
				}
			}
			const id = tmpMappedValue[pInput.PictForm.DistillingKey];
			const text = displayParts.filter((v) => v).join(': ') || tmpMappedValue[pInput.PictForm.DistillingKey];
			// This is to prevent duplicate key/values which can sometimes happen in the distilling bundle.
			if (usedKeys[id] !== text)
			{
				usedKeys[id] = text;
				tmpArray.push({
					id: tmpMappedValue[pInput.PictForm.DistillingKey],
					text: displayParts.filter((v) => v).join(': ') || tmpMappedValue[pInput.PictForm.DistillingKey],
				});
			}
		}
		tmpArray = tmpArray.sort((a, b) => {
			return a.text > b.text ? 1 : -1;
		});
		for (const t of tmpArray)
		{
			tmpListData.push(t);
		}
		return tmpResult;
	}

	/**
	 * @param {string} pDistillingKey - The distilling key that changed.
	 * @param {string} pDistillingValues - The distilling value that changed.
	 */
	_emitDistillingDataChangeEvent(pDistillingKey, pDistillingValues)
	{
		const tmpEvent = `InputDistilling:${pDistillingKey}:${pDistillingValues}`;
		const tmpTransactionGUID = this.pict.getUUID();
		this.pict.TransactionTracking.registerTransaction(tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent, tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.registerOnTransactionCompleteCallback(tmpTransactionGUID, () =>
		{
			this.pict.log.info(`DistilledInputHandler completed distilling data change event handling event ${pDistillingKey}.`);
		});
		this.pict.views.PictFormMetacontroller.finalizeTransaction(tmpTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 *
	 * @return {boolean} - Returns true.
	 */
	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index of the tabular data.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 *
	 * @return {boolean} - Returns true.
	 */
	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID, pRowIndex);
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @param {number} [pRowIndex] - The row index of the tabular data (if tabular).
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID, pRowIndex)
	{
		if (typeof pEvent !== 'string')
		{
			return;
		}
		const [ tmpType, tmpUpdatedKey ] = pEvent.split(':');
		if (tmpType !== 'InputDistilling')
		{
			return;
		}
		if (tmpUpdatedKey === pInput.PictForm.DistillingKey && pInput.PictForm.DistillingMode !== 'Projection')
		{
			return;
		}
		this.pict.log.info(`DistilledInputHandler handling event ${pEvent} for input ${pInput.Hash} in section ${pView.Hash}`);
		/** @type {import('@paviasystems/grafee').Grafee} */
		const tmpGraph = this.pict.providers.DistillingBundleManager.getLatestGraph(pInput.PictForm.DistillingBundleHash);
		let tmpChanged = false;
		if (pInput.PictForm.DistillingMode === 'Projection')
		{
			const tmpSelectedKeyValues = this._getSelectedDistillingValues(pView, pInput.PictForm.DistillingBundleHash)[pInput.PictForm.DistillingKey] || [];
			const tmpDisplayFields = (pInput.PictForm.DistillingDisplay || '').split(',').filter((v) => v !== '');
			const tmpMappedValues = tmpGraph.extractJoinedValues(pInput.PictForm.DistillingKey, tmpSelectedKeyValues, tmpDisplayFields);
			const tmpNewValue = tmpMappedValues.map((v) => tmpDisplayFields.map((f) => v[f]).filter((val) => val).join(': ') || v[pInput.PictForm.DistillingKey]).join(', ');
			pView.sectionManifest.setValueByHash(pView.getMarshalDestinationObject(), pInput.Hash, tmpNewValue);
		}
		else
		{
			const tmpListSelections = this._getSelectedDistillingValues(pView, pInput.PictForm.DistillingBundleHash, pInput.PictForm.DistillingKey);
			const tmpAvailableValues = tmpGraph.listValues(pInput.PictForm.DistillingKey, tmpListSelections);
			let tmpValue = pView.sectionManifest.getValueByHash(pView.getMarshalDestinationObject(), pInput.Hash);
			if (typeof tmpValue === 'string')
			{
				tmpValue = tmpValue.split(',').filter((v) => v !== '');
			}
			else if (tmpValue)
			{
				tmpValue = [ tmpValue ];
			}
			else
			{
				tmpValue = [];
			}
			//FIXME: doing a find here with a soft equals to deal with mixed strings and numbers... ugh - fix this upstream
			if (tmpValue.length > 0)
			{
				const tmpValidValues = tmpValue.filter((pValue) => tmpAvailableValues.find((v) => v == pValue) != null);
				if (tmpValidValues.length !== tmpValue.length)
				{
					this.pict.log.info(`DistilledInputHandler clearing value for input ${pInput.Hash} in section ${pView.Hash} due to distilling change.`);
					const tmpNewValue = tmpValidValues.length === 1 ? tmpValidValues[0] : tmpValidValues.length === 0 ? (pInput.Default ?? '') : tmpValidValues.join(',');
					pView.sectionManifest.setValueByHash(pView.getMarshalDestinationObject(), pInput.Hash, tmpNewValue);
					tmpValue = tmpValidValues;
					tmpChanged = true;
				}
			}
			if (tmpAvailableValues.length === 1 && (tmpValue.length !== 1 || tmpAvailableValues[0] != tmpValue[0]))
			{
				this.pict.log.info(`DistilledInputHandler auto-selecting only available value for input ${pInput.Hash} in section ${pView.Hash} due to distilling change.`);
				pView.sectionManifest.setValueByHash(pView.getMarshalDestinationObject(), pInput.Hash, tmpAvailableValues[0]);
				tmpChanged = true;
			}
		}
		if (tmpChanged)
		{
			if (pRowIndex != null)
			{
				pView.manualMarshalTabularDataToViewByInput(pInput, pRowIndex);
			}
			else
			{
				pView.manualMarshalDataToViewByInput(pInput);
			}
			const tmpPrevSuppressDistillingEmit = this._suppressDistillingEmit;
			this._suppressDistillingEmit = true;
			try
			{
				const tmpCommittedValue = pView.sectionManifest.getValueByHash(pView.getMarshalDestinationObject(), pInput.Hash);
				if (pRowIndex != null)
				{
					pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, tmpCommittedValue);
				}
				else
				{
					pView.setDataByInput(pInput, tmpCommittedValue);
				}
			}
			finally
			{
				this._suppressDistillingEmit = tmpPrevSuppressDistillingEmit;
			}
		}
	}

	/**
	 * @param {InstanceType<import('pict-section-form')>} pView - The view object.
	 * @param {string} pDistillingBundleHash - The distilling bundle hash.
	 * @param {string} [pIgnoreKey] - A distilling key to ignore (usually the one that triggered the event).
	 *
	 * @return {Record<string, any>} - The selected distilling values.
	 */
	_getSelectedDistillingValues(pView, pDistillingBundleHash, pIgnoreKey)
	{
		//TODO: collect via event?
		/** @type {Record<string, any>} */
		const tmpSelectedValues = {};
		const tmpManifest = this.pict.views.PictFormMetacontroller.manifestDescription;
		if (!tmpManifest)
		{
			this.pict.log.warn('DistilledInputHandler could not get manifest description to collect selected distilling values.');
			return tmpSelectedValues;
		}
		for (const tmpDescriptor of Object.values(tmpManifest.Descriptors || {}))
		{
			if (tmpDescriptor.PictForm?.DistillingBundleHash === pDistillingBundleHash && (pIgnoreKey == null || tmpDescriptor.PictForm?.DistillingKey !== pIgnoreKey))
			{
				const tmpRemoteValue = this._getInputDistillingValues(pView, tmpDescriptor);
				if (tmpRemoteValue && (!Array.isArray(tmpRemoteValue) || tmpRemoteValue.length > 0))
				{
					tmpSelectedValues[tmpDescriptor.PictForm.DistillingKey] = typeof tmpRemoteValue === 'string' && tmpRemoteValue.includes(',') ? tmpRemoteValue.split(',').filter((v) => { return v !== '' && v != '0'; }) : tmpRemoteValue;
				}
				if (tmpSelectedValues[tmpDescriptor.PictForm.DistillingKey] == 0)
				{
					delete tmpSelectedValues[tmpDescriptor.PictForm.DistillingKey];
				}
			}
		}
		return tmpSelectedValues;
	}

	/**
	 * @param {InstanceType<import('pict-section-form')>} pView - The view object.
	 * @param {Object} pDescriptor - The descriptor object.
	 *
	 * @return {any} - The distilling values for the input.
	 */
	_getInputDistillingValues(pView, pDescriptor)
	{
		//FIXME: how does this work for tabular inputs? do we just need to emit an event instead?
		return pView.sectionManifest.getValueByHash(pView.getMarshalDestinationObject(), pDescriptor.Hash);
	}
}

module.exports = DistilledInputHandler;
